import React, { useState, useContext, useEffect } from "react";
import { Box, Button, HStack, Icon, IconButton, Input, SimpleGrid, Text, Tooltip, VStack } from "@chakra-ui/react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdSearch } from "react-icons/md";
import { FaCircle } from "react-icons/fa";
import _ from "lodash";
import InputMask from "react-input-mask";
import { usePrevious, useCacheState, useApiGet } from "hooks";
import { translator } from "lib";
import { roles } from "consts";
import { SyncSelect } from "components";
import { UsersListContext } from "./index";
import * as queryFormatter from "./queryFormatter";
import CurrencyInput from "react-currency-input";

const LabelButton = ({ children, active, onClick }) => (
  <Button size="xs" colorScheme={active ? "main" : "gray"} onClick={onClick}>
    {children}
  </Button>
);

const Filter = () => {
  const { query, setQuery, setPage, loading } = useContext(UsersListContext);
  const [formData, setFormData] = useState({});
  const [visible, setVisible] = useCacheState(false, "UsersListFilter:visible");
  const [platforms] = useApiGet("/platforms");
  const [options, setOptions] = useState({
    active: [
      { value: "", label: "Selecione" },
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    anonymous: [
      { value: "", label: "Selecione" },
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    qualified: [
      { value: "", label: "Selecione" },
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    roles: _.map(roles, (role) => ({ value: role, label: translator(role) })),
    platformOss: [
      { value: "android", label: "Android" },
      { value: "ios", label: "iOS" },
    ],
  });
  const prevFormData = usePrevious(formData);

  useEffect(() => {
    const data = { platformAppVersions: [], platformOsVersions: [] };
    if (formData.platformOss?.length === 1) {
      const [key] = formData.platformOss;
      data.platformAppVersions = _.map(platforms?.[key]?.appVersions, (value) => ({ value, label: value }));
      data.platformOsVersions = _.map(platforms?.[key]?.osVersions, (value) => ({ value, label: value }));
    }
    setFormData((state) => ({ ...state, platformAppVersions: [], platformOsVersions: [] }));
    setOptions((state) => ({ ...state, ...data }));
  }, [platforms, formData.platformOss]);

  useEffect(() => {
    if (!prevFormData) setFormData(queryFormatter.queryToFormData(query));
  }, [prevFormData, query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const query = queryFormatter.formDataToQuery(formData);
    setQuery(query);
    setPage(0);
  };

  const handleCleanQuery = () => {
    setFormData({});
    setQuery({});
    setPage(0);
  };

  return (
    <form onSubmit={handleSubmit}>
      <HStack p="10px" justify="space-between">
        <HStack>
          {Object.keys(query).length > 0 && (
            <Tooltip label="Limpar filtros">
              <IconButton size="sm" colorScheme="main" variant="ghost" icon={<Icon as={FaCircle} />} onClick={handleCleanQuery} />
            </Tooltip>
          )}
          <Text fontWeight="bold">Filtros</Text>
        </HStack>
        <HStack>
          <Tooltip label="Filtrar">
            <IconButton type="submit" size="sm" icon={<Icon as={MdSearch} />} isLoading={loading} />
          </Tooltip>
          <Tooltip label="Exibir/Ocultar filtros">
            <IconButton
              size="sm"
              icon={<Icon as={visible ? MdKeyboardArrowUp : MdKeyboardArrowDown} />}
              onClick={() => setVisible(!visible)}
            />
          </Tooltip>
        </HStack>
      </HStack>

      {visible && (
        <Box p="10px">
          <SimpleGrid columns={[1, 4]} spacing={4} mb={4}>
            <VStack align="stretch">
              <LabelButton active={_.isBoolean(formData.active)} onClick={() => setFormData((state) => ({ ...state, active: undefined }))}>
                Ativos?
              </LabelButton>
              <SyncSelect
                placeholder="Selecione"
                value={options.active.find((o) => o.value === formData.active) ?? options.active?.[0]}
                options={options.active}
                onChange={({ value }) => setFormData((state) => ({ ...state, active: value }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={_.isBoolean(formData.anonymous)}
                onClick={() => setFormData((state) => ({ ...state, anonymous: undefined }))}
              >
                Anônimos?
              </LabelButton>
              <SyncSelect
                placeholder="Selecione"
                value={options.anonymous.find((o) => o.value === formData.anonymous) ?? options.anonymous?.[0]}
                options={options.anonymous}
                onChange={({ value }) => setFormData((state) => ({ ...state, anonymous: value }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={_.isBoolean(formData.qualified)}
                onClick={() => setFormData((state) => ({ ...state, qualified: undefined }))}
              >
                Qualificados?
              </LabelButton>
              <SyncSelect
                placeholder="Selecione"
                value={options.qualified.find((o) => o.value === formData.qualified) ?? options.qualified?.[0]}
                options={options.qualified}
                onChange={({ value }) => setFormData((state) => ({ ...state, qualified: value }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton active={formData.phone} onClick={() => setFormData((state) => ({ ...state, phone: undefined }))}>
                Celular
              </LabelButton>
              <Input
                as={InputMask}
                mask="55 (99) 99999-9999"
                value={formData.phone ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, phone: target.value }))}
              />
            </VStack>
          </SimpleGrid>

          <SimpleGrid columns={[1, 4]} spacing={4} mb={4}>
            <VStack align="stretch">
              <LabelButton active={formData._id} onClick={() => setFormData((state) => ({ ...state, _id: undefined }))}>
                ID
              </LabelButton>
              <Input value={formData._id ?? ""} onChange={({ target }) => setFormData((state) => ({ ...state, _id: target.value }))} />
            </VStack>
            <VStack align="stretch">
              <LabelButton active={formData.email} onClick={() => setFormData((state) => ({ ...state, email: undefined }))}>
                E-mail
              </LabelButton>
              <Input value={formData.email ?? ""} onChange={({ target }) => setFormData((state) => ({ ...state, email: target.value }))} />
            </VStack>
            <VStack align="stretch">
              <LabelButton active={formData.document} onClick={() => setFormData((state) => ({ ...state, document: undefined }))}>
                CPF
              </LabelButton>
              <Input
                as={InputMask}
                mask="999.999.999-99"
                value={formData.document ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, document: target.value }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton active={formData.cardNumber} onClick={() => setFormData((state) => ({ ...state, cardNumber: undefined }))}>
                Número do cartão
              </LabelButton>
              <Input
                value={formData.cardNumber ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, cardNumber: target.value }))}
              />
            </VStack>
          </SimpleGrid>

          <SimpleGrid columns={[1, 4]} spacing={4} mb={4}>
            <VStack align="stretch">
              <LabelButton active={formData.roles?.length > 0} onClick={() => setFormData((state) => ({ ...state, roles: undefined }))}>
                Níveis
              </LabelButton>
              <SyncSelect
                isMulti
                placeholder="Selecione"
                value={_.map(formData.roles, (value) => _.find(options.roles, (o) => o.value === value))}
                options={options.roles}
                onChange={(data) => setFormData((state) => ({ ...state, roles: _.map(data, "value") }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.platformOs?.length > 0}
                onClick={() => setFormData((state) => ({ ...state, roles: undefined }))}
              >
                S.O
              </LabelButton>
              <SyncSelect
                isMulti
                placeholder="Selecione"
                value={_.map(formData.platformOss, (value) => _.find(options.platformOss, (o) => o.value === value))}
                options={options.platformOss}
                onChange={(data) => setFormData((state) => ({ ...state, platformOss: _.map(data, "value") }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.platformAppVersion?.length > 0}
                onClick={() => setFormData((state) => ({ ...state, roles: undefined }))}
              >
                Versão S.O
              </LabelButton>
              <SyncSelect
                isMulti
                placeholder="Selecione"
                value={_.map(formData.platformOsVersions, (value) => _.find(options.platformOsVersions, (o) => o.value === value))}
                options={options.platformOsVersions}
                onChange={(data) => setFormData((state) => ({ ...state, platformOsVersions: _.map(data, "value") }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.platformAppVersion?.length > 0}
                onClick={() => setFormData((state) => ({ ...state, roles: undefined }))}
              >
                Versão App
              </LabelButton>
              <SyncSelect
                isMulti
                placeholder="Selecione"
                value={_.map(formData.platformAppVersions, (value) => _.find(options.platformAppVersions, (o) => o.value === value))}
                options={options.platformAppVersions}
                onChange={(data) => setFormData((state) => ({ ...state, platformAppVersions: _.map(data, "value") }))}
              />
            </VStack>
          </SimpleGrid>
          <SimpleGrid columns={[1, 4]} spacing={4} mb={4}>
            <VStack align="stretch">
              <LabelButton
                active={formData.birth?.start}
                onClick={() => setFormData((state) => ({ ...state, birth: { ...state.birth, start: undefined } }))}
              >
                Nascimento (início)
              </LabelButton>
              <Input
                as={InputMask}
                mask="99/99/9999"
                value={formData.birth?.start ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, birth: { ...state.birth, start: target.value } }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.birth?.end}
                onClick={() => setFormData((state) => ({ ...state, birth: { ...state.birth, end: undefined } }))}
              >
                Nascimento (fim)
              </LabelButton>
              <Input
                as={InputMask}
                mask="99/99/9999"
                value={formData.birth?.end ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, birth: { ...state.birth, end: target.value } }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.createdAt?.start}
                onClick={() => setFormData((state) => ({ ...state, birth: { ...state.birth, start: undefined } }))}
              >
                Criação (início)
              </LabelButton>
              <Input
                as={InputMask}
                mask="99/99/9999"
                value={formData.createdAt?.start ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, createdAt: { ...state.createdAt, start: target.value } }))}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData.createdAt?.end}
                onClick={() => setFormData((state) => ({ ...state, createdAt: { ...state.createdAt, end: undefined } }))}
              >
                Criação (fim)
              </LabelButton>
              <Input
                as={InputMask}
                mask="99/99/9999"
                value={formData.createdAt?.end ?? ""}
                onChange={({ target }) => setFormData((state) => ({ ...state, createdAt: { ...state.createdAt, end: target.value } }))}
              />
            </VStack>
          </SimpleGrid>
          <SimpleGrid columns={[1, 2]} spacing={4} mb={4}>
            <VStack align="stretch">
              <LabelButton
                active={formData["counters.cards"]?.start}
                onClick={() => setFormData((state) => ({ ...state, "counters.cards": { ...state["counters.cards"], start: undefined } }))}
              >
                Quantidade de cartões (início)
              </LabelButton>
              <Input
                as={CurrencyInput}
                allowNegative={true}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                value={formData["counters.cards"]?.start ?? 0}
                onChangeEvent={(_, __, start) =>
                  setFormData((state) => ({ ...state, "counters.cards": { ...state["counters.cards"], start } }))
                }
                autoFocus={false}
              />
            </VStack>
            <VStack align="stretch">
              <LabelButton
                active={formData["counters.cards"]?.end}
                onClick={() => setFormData((state) => ({ ...state, "counters.cards": { ...state["counters.cards"], end: undefined } }))}
              >
                Quantidade de cartões (fim)
              </LabelButton>
              <Input
                as={CurrencyInput}
                allowNegative={true}
                decimalSeparator=","
                thousandSeparator="."
                precision="0"
                value={formData["counters.cards"]?.end ?? 0}
                onChangeEvent={(_, __, end) =>
                  setFormData((state) => ({ ...state, "counters.cards": { ...state["counters.cards"], end } }))
                }
                autoFocus={false}
              />
            </VStack>
          </SimpleGrid>
        </Box>
      )}
    </form>
  );
};

export default Filter;
